'use client';

/**
 * Centralized API fetch utilities with automatic CSRF protection.
 *
 * All client-side code that makes mutating requests (POST, PATCH, PUT, DELETE)
 * should use these helpers instead of raw `fetch()` to ensure the CSRF token
 * is always included.
 *
 * Usage:
 *   import { apiFetch } from '@/core/utils/api-client';
 *
 *   // In a useMutation:
 *   const res = await apiFetch(`/api/users/${id}`, {
 *     method: 'PATCH',
 *     body: JSON.stringify({ name: 'New Name' }),
 *   });
 */

const MUTATING_METHODS = ['POST', 'PATCH', 'PUT', 'DELETE'];

/**
 * Read the CSRF token from the csrf_token cookie.
 */
function getCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/csrf_token=([^;]+)/);
  return match ? match[1] : null;
}

/**
 * Fetch wrapper that automatically:
 *  - Adds the x-csrf-token header on mutating requests
 *  - Sets credentials: 'include' so session cookies are sent
 *  - Sets Content-Type: application/json when a body is present (unless FormData)
 */
export async function apiFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const method = (options.method ?? 'GET').toUpperCase();

  // Build headers as a plain object for maximum compatibility
  const headers: Record<string, string> = {};

  // Copy existing headers from options
  if (options.headers) {
    if (options.headers instanceof Headers) {
      options.headers.forEach((value, key) => {
        headers[key] = value;
      });
    } else if (Array.isArray(options.headers)) {
      for (const [key, value] of options.headers) {
        headers[key] = value;
      }
    } else {
      Object.assign(headers, options.headers);
    }
  }

  // Auto-attach CSRF token for mutations
  if (MUTATING_METHODS.includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['x-csrf-token'] = csrfToken;
    }
  }

  // Set JSON content-type when there's a body (unless FormData — browser sets boundary)
  if (options.body && !(options.body instanceof FormData) && !headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }

  return fetch(url, {
    ...options,
    method,
    headers,
    credentials: 'include',
  });
}

/**
 * Convenience: make a mutating API call, parse JSON, throw on non-ok.
 * Use this inside useMutation's mutationFn for the most concise pattern.
 *
 *   mutationFn: (data) => apiMutate(`/api/resource`, 'POST', data),
 */
export async function apiMutate<T = unknown>(
  url: string,
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  body?: unknown
): Promise<T> {
  const res = await apiFetch(url, {
    method,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const text = await res.text();
    let message: string;
    try {
      message = JSON.parse(text)?.message ?? text;
    } catch {
      // Silently continue — response body is not valid JSON; use raw text as error message
      message = text;
    }
    throw new Error(message || `${method} ${url} failed (${res.status})`);
  }

  // Handle 204 No Content
  const contentType = res.headers.get('content-type');
  if (res.status === 204 || !contentType?.includes('application/json')) {
    return undefined as T;
  }

  return res.json();
}
